import OverviewFunnel from "../components/OverviewFunnel";
import InterviewFollowUps from "../components/InterviewFollowUps";
import { getSalesStatusColors } from "../utils/salesStatusColors";
import TodayAgenda from "../components/TodayAgenda";
import React, { useEffect, useMemo, useState } from "react";
import { RECRUITING_STAGES, recruitingStageColors } from "../utils/pipelineStages";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import WorkOutlineRoundedIcon from "@mui/icons-material/WorkOutlineRounded";
import useAuthController from "../hooks/useAuthController";
import MainContent from "../components/MainContent";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import { isActiveClientCompanyStatus } from "../utils/companyStatuses";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const PURPLE = "#5B4BFF";
const BORDER = "#E8E5FF";
const TEXT = "#172033";
const MUTED = "#718096";
const DAY = 24 * 60 * 60 * 1000;

const getAuthHeaders = () => {
  const token = ["token", "authToken", "jwt", "accessToken", "access_token"]
    .map((key) => localStorage.getItem(key) || sessionStorage.getItem(key))
    .find(Boolean);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const rowsFrom = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.rows || payload?.data || payload?.activities || payload?.items || [];
};

const textOf = (item) =>
  String(
    item?.stage || item?.status || item?.pipeline_stage || item?.candidate_stage || ""
  ).toLowerCase();

const includesAny = (item, words) => words.some((word) => textOf(item).includes(word));
const normalizeStageValue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");
const stageValuesOf = (item = {}) =>
  [item?.stage, item?.status, item?.pipeline_stage, item?.candidate_stage, item?.job_stage]
    .map(normalizeStageValue)
    .filter(Boolean);
const hasExactStage = (item, aliases = []) => {
  const accepted = new Set(aliases.map(normalizeStageValue));
  return stageValuesOf(item).some((value) => accepted.has(value));
};
const isRecent = (item, days = 30) => {
  const value = item?.created_at || item?.createdAt || item?.updated_at || item?.updatedAt;
  const time = value ? new Date(value).getTime() : 0;
  return Number.isFinite(time) && Date.now() - time <= days * DAY;
};
const countWith = (rows, words) => rows.filter((item) => includesAny(item, words)).length;
const getDealValue = (item) => {
  const value = item?.deal_value ?? item?.contract_value ?? item?.estimated_value ?? item?.value ?? item?.amount ?? 0;
  const normalized = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(normalized) ? normalized : 0;
};
const formatCurrency = (value) => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(Number(value || 0));
const nameOf = (item, fallback = "Untitled") =>
  item?.name || item?.title || item?.job_title || item?.company_name || item?.full_name || fallback;
const shortDate = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "Recently";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
};

const cardSx = {
  border: "1px solid #D9DEE7",
  borderRadius: 1.5,
  bgcolor: "#fff",
  boxShadow: "none",
};

function SectionCard({ title, action, children, sx = {} }) {
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 2.25 }, minWidth: 0, ...sx }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={1}>
        <Typography fontWeight={700} fontSize={15} color={TEXT}>{title}</Typography>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

export default function Overview() {
  const { user, loading, handleLogout } = useAuthController();
  const navigate = useNavigate();
  const [data, setData] = useState({
    leads: [],
    jobs: [],
    companies: [],
    candidates: [],
    activities: [],
    jobPipelineStats: {},
    recruitingStats: {},
    salesStats: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [interviewAttentionCount, setInterviewAttentionCount] = useState(0);

  useEffect(() => {
    if (!user) return undefined;
    let active = true;
    const load = async () => {
      setIsLoading(true);
      const endpoints = [
        "/api/leads?page=1&limit=1000&sort=id&order=desc",
        "/api/jobs?page=1&limit=1000",
        "/api/companies?page=1&limit=1000&include_contact_hydration=0",
        "/api/candidates",
        "/api/activities?scope=workspace&limit=100",
        "/api/jobs/stats/pipeline",
        "/api/candidates?summary=stats&has_job=1",
        "/api/leads?summary=stats",
      ];
      const results = await Promise.all(
        endpoints.map(async (path, index) => {
          try {
            const response = await fetch(`${API_BASE}${path}`, { credentials: "include", headers: getAuthHeaders() });
            if (!response.ok) return index >= 5 ? {} : [];
            const payload = await response.json();
            return index >= 5 ? payload : rowsFrom(payload);
          } catch {
            return index >= 5 ? {} : [];
          }
        })
      );
      if (active) {
        setData({
          leads: results[0],
          jobs: results[1],
          companies: results[2],
          candidates: results[3],
          activities: results[4],
          jobPipelineStats: results[5] && !Array.isArray(results[5]) ? results[5] : {},
          recruitingStats: results[6] || {},
          salesStats: results[7] || {},
        });
        setIsLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [user]);

  const dashboard = useMemo(() => {
    const { leads, jobs, companies, candidates, activities, jobPipelineStats } = data;
    const openJobs = jobs.filter((item) => !includesAny(item, ["closed", "filled", "cancel"]));
    const apiOpenJobsCount = Number(jobPipelineStats?.open_jobs);
    const openJobsCount = Number.isFinite(apiOpenJobsCount)
      ? Math.max(0, apiOpenJobsCount)
      : openJobs.length;
    // Keep this metric identical to the Companies/Clients definition so its
    // value and destination filter always represent the same records.
    const activeCompanies = companies.filter((item) => isActiveClientCompanyStatus(item?.status));
    const recentLeads = leads.filter((item) => isRecent(item));
    // Funnels represent the current stage of a record. Exact normalized
    // stage matching avoids counting unrelated values such as "not contacted"
    // or treating every candidate as Applied.
    const sales = [
      ["New", ["new", "new lead"]],
      ["Contacted", ["contacted"]],
      ["Discovery", ["discovery"]],
      ["Proposal", ["proposal"]],
      ["Client", ["client"]],
    ].map(([label, stages]) => ({
      label,
      ...getSalesStatusColors(label),
      href: `/leads?view=sales-pipeline&stage=${encodeURIComponent(label === "New" ? "New Lead" : label)}`,
      value: Number.isFinite(Number(data.salesStats[label === "Client" ? "clients" : label.toLowerCase()]))
        ? Math.max(0, Number(data.salesStats[label === "Client" ? "clients" : label.toLowerCase()]))
        : leads.filter((lead) => hasExactStage(lead, stages)).length,
    }));
    const recruiting = RECRUITING_STAGES.filter(({ id }) => id !== "rejected").map(({ id, label }) => ({
      id,
      label,
      value: Math.max(0, Number(data.recruitingStats?.[id]) || 0),
      ...recruitingStageColors(id),
      href: `/candidates?view=pipeline&stage=${encodeURIComponent(label)}`,
    }));
    const owners = [...jobs, ...companies, ...candidates].reduce((map, item) => {
      const owner = item?.owner_name || item?.owner || item?.recruiter_name || item?.assigned_team_member || item?.assigned_to;
      if (owner) map[owner] = (map[owner] || 0) + 1;
      return map;
    }, {});
    const attention = [
      { label: "AI matches awaiting review", count: activities.filter((item) => includesAny(item, ["review", "pending"])).length, color: "#E23B3B" },
      { label: "Candidates awaiting HM feedback", count: countWith(candidates, ["feedback"]), color: "#FF7A00" },
      { label: "Jobs have no candidates presented", count: openJobs.filter((item) => !Number(item?.candidate_count || item?.candidates_count)).length, color: "#EAB308" },
      { label: "Offers pending 7+ days", count: candidates.filter((item) => includesAny(item, ["offer"]) && !isRecent(item, 7)).length, color: "#E23B3B" },
      { label: "Proposals not viewed in 10+ days", count: leads.filter((item) => includesAny(item, ["proposal"]) && !isRecent(item, 10)).length, color: "#FF7A00" },
      { label: "Assessments awaiting review", count: activities.filter((item) => includesAny(item, ["assessment"])).length, color: "#9A52F5" },
    ];
    const forecastValue = activeCompanies.reduce((sum, item) => sum + getDealValue(item), 0);
    const monthRevenue = activeCompanies.filter((item) => isRecent(item)).reduce((sum, item) => sum + getDealValue(item), 0);
    return { openJobs, openJobsCount, activeCompanies, recentLeads, sales, recruiting, owners, attention, activities, forecastValue, monthRevenue };
  }, [data]);

  if (loading || !user) return <Box p={4}><CircularProgress size={24} /></Box>;

  const viewButton = (label, to) => <Button size="small" onClick={() => navigate(to)} endIcon={<ArrowForwardRoundedIcon />} sx={{ color: PURPLE, minWidth: 0, px: 0, fontWeight: 500 }}>{label}</Button>;


  return (
    <MainContent user={user} onLogout={handleLogout}>
      <ListTableTabsTemplate
        rootSx={{ width: "100%", maxWidth: "100%" }}
        shellSx={{
          pb: 0,
          border: "0 !important",
          borderRadius: "0 !important",
          backgroundColor: "transparent !important",
          overflow: "visible",
        }}
        leftColumnSx={{ backgroundColor: "transparent" }}
        contentSection={
          <Box sx={{ width: "100%", maxWidth: "100%", px: 0, pb: 3 }}>
        {isLoading && <Typography fontSize={12} color={MUTED} sx={{ mb: 1 }}>Refreshing dashboard data…</Typography>}

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "repeat(4, minmax(0, 1fr))" }} gap={2.25} mb={2.25}>
          <SectionCard title={<Stack direction="row" alignItems="center" spacing={0.8}><ErrorOutlineRoundedIcon sx={{ fontSize: 20, color: "#E23B3B" }} /><span>Needs Attention</span><Box component="span" sx={{ px: 0.7, py: 0.15, borderRadius: 5, bgcolor: "#FFF0F2", color: "#E23B3B", fontSize: 11, fontWeight: 800 }}>{interviewAttentionCount + dashboard.attention.reduce((sum, item) => sum + item.count, 0)}</Box></Stack>}>
            <InterviewFollowUps compact onCountChange={setInterviewAttentionCount} />
            <Stack spacing={1.25}>{dashboard.attention.map((item) => <Stack key={item.label} direction="row" alignItems="center" justifyContent="space-between" gap={1}><Stack direction="row" spacing={1} alignItems="center" minWidth={0}><Box width={7} height={7} flex="0 0 auto" borderRadius="50%" bgcolor={item.color} /><Typography fontSize={12.5} color="#687080" noWrap>{item.count} {item.label}</Typography></Stack><Typography fontSize={12.5} fontWeight={800} color={item.color}>{item.count}</Typography></Stack>)}</Stack>
            <Box textAlign="center" mt={2}>{viewButton("View all", "/activities")}</Box>
          </SectionCard>
          <SectionCard title={<Stack direction="row" alignItems="center" spacing={0.8}><CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: PURPLE }} /><span>Today’s Agenda</span></Stack>}>
            <TodayAgenda />
          </SectionCard>
          <SectionCard title="Sales Funnel" sx={{ display: "flex", flexDirection: "column", alignSelf: "start", minHeight: 374, borderColor: "#D2D8DE", p: 3, "& > .MuiStack-root": { mb: 2 } }}>
            <OverviewFunnel entries={dashboard.sales} scale="logarithmic" />
            <Box textAlign="center" sx={{ mt: "auto", pt: 3 }}>
              <Button onClick={() => navigate("/leads?view=sales-pipeline")} sx={{ color: "#9C4FFF", fontSize: 12, fontWeight: 500, textTransform: "none" }}>View full pipeline</Button>
            </Box>
          </SectionCard>
          <SectionCard title="Recruiting Funnel" sx={{ display: "flex", flexDirection: "column", alignSelf: "start", minHeight: 374, borderColor: "#D2D8DE", p: 3, "& > .MuiStack-root": { mb: 1.25 } }}>
            <OverviewFunnel entries={dashboard.recruiting} rowGap={0.25} />
            <Box textAlign="center" sx={{ mt: "auto", pt: 1.75, lineHeight: 1.5 }}>
              <Link component={RouterLink} to="/candidates?view=pipeline" underline="hover" sx={{ color: "#9C4FFF", fontSize: 12, fontWeight: 500 }}>View full pipeline</Link>
            </Box>
          </SectionCard>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "repeat(4, minmax(0, 1fr))" }} gap={2.25} mb={2.25}>
          <SectionCard title="Revenue & Forecast" action={<Button variant="outlined" endIcon={<KeyboardArrowDownRoundedIcon />} sx={{ borderColor: "#D9DDE5", color: "#596170", borderRadius: 1.25, textTransform: "none", fontWeight: 400, fontSize: 11 }}>This Month</Button>}>
            <Stack direction="row" spacing={2} mb={1.7}><Box flex={1}><Typography fontSize={10.5} color={MUTED}>Revenue This Month</Typography><Typography fontSize={23} fontWeight={800} color={TEXT}>{formatCurrency(dashboard.monthRevenue)}</Typography><Typography fontSize={10.5} color="#1BAA72">Live from active deals</Typography></Box><Box flex={1}><Typography fontSize={10.5} color={MUTED}>Forecast Pipeline</Typography><Typography fontSize={23} fontWeight={800} color={TEXT}>{formatCurrency(dashboard.forecastValue)}</Typography><Typography fontSize={10.5} color="#1BAA72">Current pipeline value</Typography></Box></Stack>
            <Box sx={{ borderTop: "1px solid #E8EAF0", pt: 1.25, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 0.75, textAlign: "center" }}>{[["MRR", dashboard.monthRevenue], ["YTD Revenue", dashboard.monthRevenue], ["Avg Deal Size", dashboard.activeCompanies.length ? dashboard.forecastValue / dashboard.activeCompanies.length : 0], ["Win Rate", dashboard.activeCompanies.length ? `${Math.round((data.companies.filter((item) => includesAny(item, ["won", "active"])).length / dashboard.activeCompanies.length) * 100)}%` : "0%"]].map(([label, value]) => <Box key={label}><Typography fontSize={9.5} color="#A0A7B5">{label}</Typography><Typography fontSize={11.5} fontWeight={800} color={TEXT}>{typeof value === "number" ? formatCurrency(value) : value}</Typography></Box>)}</Box>
            <Box textAlign="center" mt={1.45}>{viewButton("View revenue report", "/clients")}</Box>
          </SectionCard>
          <SectionCard title="Team Snapshot" action={viewButton("View Team", "/team")}>
            <Stack direction="row" spacing={1.5} mb={1.1}><Typography fontSize={10.5} color="#627080"><Box component="span" sx={{ color: "#1DB878" }}>•</Box> {Object.keys(dashboard.owners).length} Online</Typography><Typography fontSize={10.5} color="#627080"><Box component="span" sx={{ color: "#9A52F5" }}>•</Box> {Object.keys(dashboard.owners).length} Active</Typography><Typography fontSize={10.5} color="#627080"><Box component="span" sx={{ color: "#FF7A00" }}>•</Box> Util</Typography></Stack><Divider sx={{ borderColor: "#E8EAF0", mb: 1 }} /><Typography fontSize={10.5} color="#A0A7B5" mb={0.8}>Top Performers (This Month)</Typography>
            {Object.entries(dashboard.owners).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([owner, count]) => <Stack key={owner} direction="row" alignItems="center" justifyContent="space-between" mb={1.15}><Stack direction="row" spacing={1} alignItems="center"><Avatar sx={{ width: 26, height: 26, bgcolor: "#EEEAFE", color: PURPLE, fontSize: 11 }}>{owner.slice(0, 2).toUpperCase()}</Avatar><Typography fontSize={13} color={TEXT}>{owner}</Typography></Stack><Typography fontSize={12} color={MUTED}>{count} assigned</Typography></Stack>)}
            {!Object.keys(dashboard.owners).length && <Typography fontSize={13} color={MUTED}>Assignments will appear as your team starts working.</Typography>}
            <Box textAlign="center" mt={1}>{viewButton("View full team performance", "/team")}</Box>
          </SectionCard>
          <SectionCard title="Open jobs requiring attention" action={viewButton("All jobs", "/jobs")}>
            {dashboard.openJobs.slice(0, 3).map((job) => <Box key={job.id} mb={1.2}><Typography fontSize={13} color={TEXT} noWrap>{nameOf(job)}</Typography><Typography fontSize={11.5} color={MUTED} noWrap>{job.company_name || job.company || "No company assigned"}</Typography></Box>)}
            {!dashboard.openJobs.length && <Typography fontSize={13} color={MUTED}>No open jobs right now.</Typography>}
          </SectionCard>
          <SectionCard title="Active Deals" action={viewButton("View All Deals", "/clients")}>
            {dashboard.activeCompanies.slice(0, 4).map((company) => <Box key={company.id} mb={1.25}><Stack direction="row" justifyContent="space-between" gap={1}><Typography fontSize={12.5} fontWeight={700} color={TEXT} noWrap>{nameOf(company)}</Typography><Typography fontSize={12} fontWeight={800} color={TEXT}>{formatCurrency(getDealValue(company))}</Typography></Stack><Typography fontSize={10.5} color={MUTED} noWrap>{company.stage || company.status || "Active deal"}</Typography><Box mt={0.55} ml="48%" height={5} bgcolor="#EFE8FF" borderRadius={5}><Box width={`${dashboard.forecastValue ? Math.max(3, (getDealValue(company) / dashboard.forecastValue) * 100) : 0}%`} height="100%" bgcolor="#9A52F5" borderRadius={5} /></Box></Box>)}
            {!dashboard.activeCompanies.length && <Typography fontSize={13} color={MUTED}>No active deals yet.</Typography>}
          </SectionCard>
        </Box>

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "1.1fr 1fr 1fr" }} gap={2.25}>
          <SectionCard title="Recent activity" action={viewButton("View all", "/activities")}>
            <Stack spacing={1.35}>{dashboard.activities.slice(0, 4).map((activity) => <Stack key={activity.id} direction="row" spacing={1.1}><CheckCircleOutlineRoundedIcon sx={{ color: "#32B67A", fontSize: 18, mt: 0.2 }} /><Box minWidth={0}><Typography fontSize={13} color={TEXT}>{nameOf(activity, activity?.description || "Activity updated")}</Typography><Typography fontSize={11.5} color={MUTED}>{shortDate(activity?.updated_at || activity?.created_at)}</Typography></Box></Stack>)}</Stack>
            {!dashboard.activities.length && <Typography fontSize={13} color={MUTED}>Activity will appear here as work progresses.</Typography>}
          </SectionCard>
          <SectionCard title={<Stack direction="row" spacing={0.8} alignItems="center"><span>AI insights</span><Typography component="span" fontSize={10} color={PURPLE} bgcolor="#F1EDFF" px={0.75} py={0.2} borderRadius={1}>BETA</Typography></Stack>} action={<AutoAwesomeRoundedIcon sx={{ color: PURPLE }} />}>
            <Typography fontSize={13} color={TEXT} mb={1.1}>{dashboard.openJobs.length ? `${dashboard.openJobs.length} open job${dashboard.openJobs.length === 1 ? "" : "s"} are ready for recruiting activity.` : "Your pipeline is ready for new opportunities."}</Typography>
            <Typography fontSize={12.5} color={MUTED}>Use the activity board to review AI work and keep momentum moving.</Typography>
          </SectionCard>
          <SectionCard title="Continue working" action={<MoreHorizRoundedIcon sx={{ color: MUTED }} />}>
            <Stack spacing={1.15}>{[
              ["Review activities", "/activities", NotificationsOutlinedIcon],
              ["Manage open jobs", "/jobs", WorkOutlineRoundedIcon],
              ["View team workload", "/team", GroupsOutlinedIcon],
            ].map(([label, to, Icon]) => <Button key={label} variant="outlined" onClick={() => navigate(to)} startIcon={<Icon />} endIcon={<ArrowForwardRoundedIcon />} sx={{ justifyContent: "space-between", borderRadius: 2, color: TEXT, borderColor: BORDER, fontSize: 12.5, "&:hover": { borderColor: PURPLE, bgcolor: "#FAF9FF" } }}>{label}</Button>)}</Stack>
          </SectionCard>
        </Box>
          </Box>
        }
      />
    </MainContent>
  );
}
